import { STALE_TIME, GC_TIME, QUERY_PRESETS } from "@/queries/queryTimeConfig";

/**
 * HOUSEKEEPING API CONFIGURATION
 * Keys EXACTLY match controllers exposed by /api/v1/housekeeping
 */
export const housekeepingConfig = {
  /* ------------------------------- GET ----------------------------------- */
  getTasksByOrganization: {
    key: "getTasksByOrganization",
    url: "/api/v1/housekeeping/tasks",
    method: "GET",
    options: {
      ...QUERY_PRESETS.LIST,
    },
  },

  getAllRolesForHousekeeping: {
    key: "getAllRolesForHousekeeping",
    url: "/api/v1/housekeeping/roles",
    method: "GET",
    options: {
      ...QUERY_PRESETS.SHORT,
    },
  },

  getUsersByRoleForHousekeeping: {
    key: "getUsersByRoleForHousekeeping",
    url: "/api/v1/housekeeping/staff-by-role",
    method: "GET",
    options: {
      ...QUERY_PRESETS.SHORT,
    },
  },

  getStaffActiveTasks: {
    key: "getStaffActiveTasks",
    url: (data) => `/api/v1/housekeeping/tasks/staff/${data.staffUserId}/active`,
    method: "GET",
    options: {
      ...QUERY_PRESETS.LIST,
    },
  },

  getPropertyHousekeepingStats: {
    key: "getPropertyHousekeepingStats",
    url: "/api/v1/housekeeping/stats",
    method: "GET",
    options: {
      ...QUERY_PRESETS.SHORT,
    },
  },

  getAllRoomsForHousekeeping: {
    key: "getAllRoomsForHousekeeping",
    url: "/api/v1/housekeeping/rooms",
    method: "GET",
    options: {
      ...QUERY_PRESETS.SHORT,
    },
  },

  getHousekeepingStaffForProperty: {
    key: "getHousekeepingStaffForProperty",
    url: "/api/v1/housekeeping/staff",
    method: "GET",
    options: {
      ...QUERY_PRESETS.SHORT,
    },
  },

  getAllHousekeepingTasks: {
    key: "getAllHousekeepingTasks",
    url: "/api/v1/housekeeping/tasks/all",
    method: "GET",
    options: {
      ...QUERY_PRESETS.LIST,
    },
  },

  createHousekeepingTask: {
    key: "createHousekeepingTask",
    url: "/api/v1/housekeeping/tasks",
    method: "POST",
    options: {
      retry: 1,
      invalidateKeys: [
        "getAllHousekeepingTasks",
        "getPropertyHousekeepingStats",
        "getStaffActiveTasks",
      ],
    },
  },

  getMyAssignedTasks: {
    key: "getMyAssignedTasks",
    url: "/api/v1/housekeeping/tasks/my-assigned",
    method: "GET",
    options: {
      ...QUERY_PRESETS.SHORT,
    },
  },

  updateTaskAssignmentAndStart: {
    key: "updateTaskAssignmentAndStart",
    url: (data) => `/api/v1/housekeeping/tasks/${data.taskId}/assign-and-start`,
    method: "POST",
    options: {
      retry: 2,
      invalidateKeys: ["getTasksByOrganization", "getStaffActiveTasks"],
    },
  },

  updateAssignTaskByStaff: {
    key: "updateAssignTaskByStaff",
    url: (data) => `/api/v1/housekeeping/tasks/${data.taskId}/self-assign`,
    method: "POST",
    options: {
      retry: 2,
      invalidateKeys: [
        "getMyAssignedTasks",
        "getTasksByOrganization",
        "getStaffActiveTasks",
      ],
    },
  },

  verifyOrCancelTask: {
    key: "verifyOrCancelTask",
    url: (data) => `/api/v1/housekeeping/tasks/${data.taskId}/verify`,
    method: "POST",
    options: {
      retry: 2,
      invalidateKeys: [
        "getMyAssignedTasks",
        "getTasksByOrganization",
        "getStaffActiveTasks",
        "getCompletedTasksForSupervisor",
        "getPropertyHousekeepingStats",
      ],
    },
  },

  updateHousekeepingTask: {
    key: "updateHousekeepingTask",
    url: (data) => `/api/v1/housekeeping/tasks/${data.taskId}`,
    method: "PUT",
    options: {
      retry: 2,
      invalidateKeys: [
        "getStaffActiveTasks",
        "getTasksByOrganization",
        "getCompletedTasksForSupervisor",
      ],
    },
  },

  assignHousekeepingTask: {
    key: "assignHousekeepingTask",
    url: (data) => `/api/v1/housekeeping/tasks/${data.taskId}/assign`,
    method: "POST",
    options: {
      retry: 1,
      invalidateKeys: [
        "getAllHousekeepingTasks",
        "getPropertyHousekeepingStats",
        "getStaffActiveTasks",
      ],
    },
  },


  getCompletedTasksForSupervisor: {
    key: "getCompletedTasksForSupervisor",
    url: "/api/v1/housekeeping/tasks/completed-for-supervisor",
    method: "GET",
    options: {
      staleTime: STALE_TIME.SHORT,
      gcTime: GC_TIME.MEDIUM,
      refetchOnWindowFocus: true,
      keepPreviousData: true,
    },
  },

  getHousekeepingLogs: {
    key: "getHousekeepingLogs",
    url: (data) => `/api/v1/housekeeping/tasks/${data.taskId}/logs`,
    method: "GET",
    options: {
      ...QUERY_PRESETS.SHORT,
      keepPreviousData: true,
    },
  },

  getTaskAnalytics: {
    key: "getTaskAnalytics",
    url: "/api/v1/housekeeping/analytics",
    method: "GET",
    options: {
      ...QUERY_PRESETS.SHORT,
    },
  },

  rejectHousekeepingTask: {
    key: "rejectHousekeepingTask",
    url: (data) => `/api/v1/housekeeping/tasks/${data.taskId}/reject`,
    method: "POST",
    options: {
      retry: 1,
      invalidateKeys: [
        "getAllHousekeepingTasks",
        "getTasksByOrganization",
        "getPropertyHousekeepingStats",
        "getCompletedTasksForSupervisor",
        "getMyAssignedTasks",
        "getStaffActiveTasks",
      ],
    },
  },

  cancelHousekeepingTask: {
    key: "cancelHousekeepingTask",
    url: (data) => `/api/v1/housekeeping/tasks/${data.taskId}/cancel`,
    method: "POST",
    options: {
      retry: 1,
      invalidateKeys: [
        "getAllHousekeepingTasks",
        "getTasksByOrganization",
        "getPropertyHousekeepingStats",
        "getCompletedTasksForSupervisor",
      ],
    },
  },

  requestCancelTask: {
    key: "requestCancelTask",
    url: (data) => `/api/v1/housekeeping/tasks/${data.taskId}/request-cancel`,
    method: "POST",
    options: {
      retry: 1,
      invalidateKeys: [
        "getMyAssignedTasks",
        "getStaffActiveTasks",
        "getAllHousekeepingTasks",
        "getCancelRequestedTasks",
      ],
    },
  },

  resolveCancelRequest: {
    key: "resolveCancelRequest",
    url: (data) => `/api/v1/housekeeping/tasks/${data.taskId}/resolve-cancel`,
    method: "POST",
    options: {
      retry: 1,
      invalidateKeys: [
        "getCancelRequestedTasks",
        "getAllHousekeepingTasks",
        "getPropertyHousekeepingStats",
        "getStaffActiveTasks",
        "getMyAssignedTasks",
      ],
    },
  },

  getCancelRequestedTasks: {
    key: "getCancelRequestedTasks",
    url: "/api/v1/housekeeping/tasks/cancel-requests",
    method: "GET",
    options: {
      ...QUERY_PRESETS.SHORT,
    },
  },

  updateChecklistItem: {
    key: "updateChecklistItem",
    url: (data) =>
      `/api/v1/housekeeping/tasks/${data.taskId}/checklist/${data.itemId}`,
    method: "PATCH",
    options: {
      retry: 1,
      invalidateKeys: ["getAllHousekeepingTasks"],
    },
  },
};
